import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { fmtDate } from "@/lib/time";
import { Users, Building2, CheckCircle2, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  new:         "bg-gray-500/10 text-gray-400",
  contacted:   "bg-blue-500/10 text-blue-400",
  qualified:   "bg-purple-500/10 text-purple-400",
  proposal:    "bg-amber-500/10 text-amber-400",
  closed_won:  "bg-emerald-500/10 text-emerald-400",
  closed_lost: "bg-red-500/10 text-red-400",
};

export default function PartnerReferrals() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/sm/partners/portal/referrals?page=${page}&limit=20`, { withCredentials: true });
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const converted = items.filter(i => i.status === "closed_won").length;

  return (
    <div className="space-y-6" data-testid="partner-referrals-page">
      <div>
        <h1 className="font-serif text-3xl text-white font-light">My Referrals</h1>
        <p className="text-sm text-gray-500 mt-1">{total} total leads · {converted} on this page converted</p>
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-16 bg-white/5 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl p-12 text-center">
          <Users className="w-8 h-8 text-gray-700 mx-auto mb-3" strokeWidth={1} />
          <p className="text-gray-500 text-sm">No referrals yet</p>
          <p className="text-gray-700 text-xs mt-1">Refer a law firm to start earning commissions</p>
        </div>
      ) : (
        <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl overflow-hidden">
          <div className="divide-y divide-white/5">
            {items.map(item => (
              <div key={item.id} className="flex items-center justify-between px-5 py-4 hover:bg-white/[0.02] transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-4 h-4 text-gray-500" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-sm text-white">{item.firm_name || item.name}</div>
                    <div className="text-[11px] text-gray-500">
                      {item.created_at ? fmtDate(item.created_at) : "—"}
                      {item.tenant_id && <span className="ml-2 text-emerald-500">● Live tenant</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {item.status === "closed_won" ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" strokeWidth={1.5} />
                  ) : (
                    <Clock className="w-4 h-4 text-gray-600" strokeWidth={1.5} />
                  )}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize font-medium ${STATUS_BADGE[item.status] || "bg-gray-500/10 text-gray-400"}`}>
                    {(item.status || "new").replace("_", " ")}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pagination */}
      {Math.ceil(total / 20) > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-600">Page {page} of {Math.ceil(total / 20)}</span>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1.5 text-xs rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 disabled:opacity-30">Prev</button>
            <button onClick={() => setPage(p => Math.min(Math.ceil(total / 20), p + 1))} disabled={page === Math.ceil(total / 20)}
              className="px-3 py-1.5 text-xs rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 disabled:opacity-30">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
